import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Platform } from '@app/classes';
import { FeatureFlag } from '@app/enums';
import { EventType } from '@app/enums/events.type';
import { Employee } from '@app/models/employee/employee.model';
import { TimeOffRequest, TimeOffRequestStatus } from '@app/models/time-off-v3/time-off-request.model';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { LocalStorageService } from '@app/services/local-storage/local-storage.service';
import { NotifyService } from '@app/services/notify.service';
import { Day } from '@dashboard/components/upcoming-events/day';
import { CalendarEvent } from '@models/common/calendar-event.model';
import { TranslateService } from '@ngx-translate/core';
import {
    addDays,
    addYears,
    areRangesOverlapping,
    compareAsc,
    differenceInYears,
    endOfDay,
    format,
    getYear,
    isPast,
    isSameYear,
    parse,
    setYear,
    startOfDay,
    subMinutes,
} from 'date-fns';

const MAX_EVENTS = 4;
type LocalStorageFilters = {
    id: number;
    filterValue: string | number | null;
};
const localStorageDashboardEventFiltersKey = 'dashboard-event-filters';

@Injectable()
export class EventService {
    eventType: EventType = 'all';
    days: Day[] = [];
    firstTwoDays: Day[] = [];
    filterOptions: number | string | null = null;
    next7Days: Day[] = [];
    next7DaysDateGrouping: Day = new Day();
    hasImprovedHomepageFlag = false;
    hasBirthdayVisibilityFlag = false;
    timeOffRequests: TimeOffRequest[] = [];

    private events: CalendarEvent[] = [];
    private readonly startAt: string;
    private readonly endAt: string;

    constructor(
        private auth: AuthService,
        private featureService: FeatureService,
        private localStorage: LocalStorageService,
        private notify: NotifyService,
        private translateService: TranslateService
    ) {
        this.startAt = format(startOfDay(new Date()), 'YYYY-MM-DD');
        this.endAt = format(addDays(this.startAt, 7), 'YYYY-MM-DD');

        this.auth.onLogin.pipe(takeUntilDestroyed()).subscribe(() => {
            this.applyFilters();
        });

        this.auth.onLogout.pipe(takeUntilDestroyed()).subscribe(() => {
            this.filterOptions = null;
        });

        this.applyFilters();
    }

    /**
     * Get the filter values saved in local storage and applies it for the current user.
     */
    async applyFilters(): Promise<void> {
        const localFiltersArray = await this.getSavedFilters();

        const currentEmployeeFilter = localFiltersArray?.find((localFilter) => {
            return localFilter.id === this.auth.employee.id;
        });

        if (currentEmployeeFilter) {
            this.filterOptions = currentEmployeeFilter.filterValue;
        }
    }

    createDays(hasImprovedHomepageFlag: boolean): void {
        const daysRange: number[] = [0, 1, 2, 3, 4, 5, 6].concat(hasImprovedHomepageFlag ? [7, 8] : []);

        this.days = daysRange.map((daysFromToday) => {
            const day = new Day();
            day.date = addDays(new Date(), daysFromToday);
            day.isToday = daysFromToday <= 0;
            day.numberOfEventsViewableWhenNotExpanded = MAX_EVENTS;
            return day;
        });
    }

    async loadAllEvents(departmentId: number | null): Promise<void> {
        let request = TimeOffRequest.with(['employee', 'timeOffPolicy.timeOffType'])
            .where('startAt', {
                to: format(this.endOfPeriod, 'YYYY-MM-DD'),
            })
            .where('endAt', { from: format(this.startOfPeriod, 'YYYY-MM-DD') })
            .where('status', TimeOffRequestStatus.approved);

        if (departmentId !== null) {
            request = request.where('departmentId', departmentId);
        } else if (this.filterOptions !== null) {
            request = request.where('departmentId', this.filterOptions);
        }

        try {
            const hasTimeOff = this.auth.company.modules.some((m) => m.name === Platform.modules.timeOff);

            const miscEvents = await this.getAllEvents(departmentId);

            if (!hasTimeOff) {
                this.events = miscEvents;
                this.putEventsInDays();
            }

            const [timeOffRequests] = await request.all();

            const timeOffRequestEvents = timeOffRequests.map((timeOffRequest: TimeOffRequest) =>
                timeOffRequest.toCalendarEvent(this.auth)
            );

            this.events = [...timeOffRequestEvents, ...miscEvents];

            this.hasImprovedHomepageFlag ? this.putImprovedEventsInDays() : this.putEventsInDays();
        } catch (error) {
            this.notify.error(error);
        }
    }

    async loadBirthdayEvents(departmentId: number | null): Promise<void> {
        const [birthdays] = await Promise.all([this.getBirthdays(departmentId)]);
        this.events = [...birthdays];
        this.putImprovedEventsInDays();
    }

    async loadAnniversaryEvents(departmentId: number | null): Promise<void> {
        const [anniversaries] = await Promise.all([this.getAnniversaries(departmentId)]);
        const anniversaryEvents = anniversaries.filter((event) => event.type === 'anniversary');
        this.events = [...anniversaryEvents];
        this.putImprovedEventsInDays();
    }

    async loadAwayEvents(departmentId: number | null): Promise<void> {
        let request = TimeOffRequest.with(['employee', 'timeOffPolicy.timeOffType'])
            .where('startAt', {
                to: format(this.endOfPeriod, 'YYYY-MM-DD'),
            })
            .where('endAt', { from: format(this.startOfPeriod, 'YYYY-MM-DD') })
            .where('status', TimeOffRequestStatus.approved);

        if (departmentId !== null) {
            request = request.where('departmentId', departmentId);
        }

        [this.timeOffRequests] = await request.all();

        const timeOffRequestEvents = this.timeOffRequests.map((timeOffRequest: TimeOffRequest) =>
            timeOffRequest.toCalendarEvent(this.auth)
        );

        this.events = [...timeOffRequestEvents];
        this.putImprovedEventsInDays();
    }

    async loadNewHireEvents(departmentId: number | null): Promise<void> {
        const [anniversaries] = await Promise.all([this.getAnniversaries(departmentId)]);
        const anniversaryEvents = anniversaries.filter((event) => event.type === 'newHire');
        this.events = [...anniversaryEvents];
        this.putImprovedEventsInDays();
    }

    getDays(): Day[] {
        return this.days;
    }

    getFirstTwoDays(): Day[] {
        return this.firstTwoDays;
    }

    getNext7Days(): Day {
        return this.next7DaysDateGrouping;
    }

    async setFilterOptions(departmentId: number | string | null): Promise<void> {
        this.filterOptions = departmentId;

        // This logic is for storing data to local storage for filters.
        const localFiltersArray = await this.getSavedFilters();

        const updatedFilters = this.updateFilterArray(localFiltersArray, {
            id: this.auth.employee.id,
            filterValue: this.filterOptions,
        });

        this.localStorage.store(localStorageDashboardEventFiltersKey, updatedFilters);
    }

    async setFeatureflags(): Promise<void> {
        const [hasImprovedHomepageFlag, hasBirthdayVisibilityFlag] = await Promise.all([
            this.featureService.has(FeatureFlag.improvedHomepage),
            this.featureService.has(FeatureFlag.birthdayVisibility),
        ]);

        this.hasImprovedHomepageFlag = hasImprovedHomepageFlag;
        this.hasBirthdayVisibilityFlag = hasBirthdayVisibilityFlag;
    }

    private updateFilterArray(filters: LocalStorageFilters[], newFilter: LocalStorageFilters): LocalStorageFilters[] {
        const existingFilterIndex = filters.findIndex((filter) => filter.id === newFilter.id);

        if (existingFilterIndex !== -1) {
            // Update existing filter
            filters[existingFilterIndex].filterValue = newFilter.filterValue;
        } else {
            // Add new filter
            filters.push(newFilter);
        }

        return filters;
    }

    private get startOfPeriod(): Date | null {
        return this.days.length ? this.days[0].date : null;
    }

    private get endOfPeriod(): Date | null {
        return this.days.length ? addDays(this.days[this.days.length - 1].date, 1) : null;
    }

    private async getAllEvents(departmentId: number | null): Promise<any> {
        const [birthdays, anniversaries] = await Promise.all([
            this.getBirthdays(departmentId),
            this.getAnniversaries(departmentId),
        ]);

        return [...birthdays, ...anniversaries].sort((a: any, b: any): number => compareAsc(a.startAt, b.startAt));
    }

    private async getBirthdays(departmentId: number | null): Promise<any[]> {
        if (!this.auth.canAny(Employee.birthdayPermission.view)) {
            return [];
        }

        let request = Employee.param('company', this.auth.company.id)
            .with('company')
            .where('birthday', { startAt: this.startAt, endAt: this.endAt });

        if (this.hasBirthdayVisibilityFlag) {
            request = request.where('onlyPublicBirthdays', 1);
        }

        if (departmentId !== null) {
            request = request.where('department_id', departmentId);
        } else if (this.filterOptions !== null) {
            request = request.where('department_id', this.filterOptions);
        }

        try {
            const [birthdayEmployees] = await request.all();

            return this.birthdayToEvents(birthdayEmployees);
        } catch (e) {
            console.warn('Could not load employees with birthday filter');
            console.warn(e);

            return [];
        }
    }

    private birthdayToEvents(employees: Employee[]): any[] {
        return employees.map((employee) => {
            if (!employee.firstName || !employee.lastName || !employee.birthMonth || !employee.birthDay) {
                throw new Error('invalid employee');
            }

            const name = employee.firstName + ' ' + employee.lastName;
            const text = this.translateService.instant('dashboard.someBirthday', { name: name });

            const birthDay = new Date(getYear(new Date()), employee.birthMonth - 1, employee.birthDay);

            // handle the last week of the year
            if (isPast(birthDay)) {
                addYears(birthDay, 1);
            }

            return {
                employeeId: employee.id,
                pseudoEmployee: {
                    avatarId: employee.avatarId,
                    firstName: employee.firstName,
                    lastName: employee.lastName,
                },
                upcomingEventTitle: text,
                upcomingEventDescription: 'dashboard.happyBirthday',
                startAt: birthDay,
                endAt: endOfDay(birthDay),
                type: 'birthday',
            };
        });
    }

    private async getAnniversaries(departmentId: number | null): Promise<any[]> {
        const doNotFetchAnniversaryEvents = this.auth.cant(Employee.eventFeedAnniversariesPermission);

        if (doNotFetchAnniversaryEvents) {
            return [];
        }

        let request = Employee.param('company', this.auth.company.id)
            .with('company')
            .where('anniversary', { startAt: this.startAt, endAt: this.endAt });

        if (departmentId !== null) {
            request = request.where('department_id', departmentId);
        } else if (this.filterOptions !== null) {
            request = request.where('department_id', this.filterOptions);
        }

        try {
            const [anniversaryEmployees] = await request.all();

            return this.anniversaryToEvents(anniversaryEmployees);
        } catch (e) {
            console.warn('Could not load employees with anniversary filter');
            console.warn(e);

            return [];
        }
    }

    private anniversaryToEvents(employees: Employee[]): any[] {
        return employees.map((employee) => {
            if (!employee.firstName || !employee.lastName) {
                throw new Error('invalid employee');
            }

            const name = employee.firstName + ' ' + employee.lastName;
            const now = new Date();
            const isFirstDay = isSameYear(employee.hiredAt, now);
            const date = setYear(parse(employee.hiredAt), getYear(now));
            const yearsAtCompany = differenceInYears(date, employee.hiredAt);
            const text = isFirstDay
                ? this.translateService.instant('dashboard.newHireTitle', { name: name })
                : this.hasImprovedHomepageFlag
                  ? this.translateService.instant('dashboard.newWorkAnniversaryTitle', {
                        name: name,
                        yearsAtCompany: yearsAtCompany,
                    })
                  : this.translateService.instant('dashboard.workAnniversaryTitle', {
                        name: name,
                    });

            const description = isFirstDay
                ? 'dashboard.newHire'
                : yearsAtCompany > 1
                  ? this.translateService.instant('dashboard.celebratingYear', {
                        number: yearsAtCompany,
                        company: employee.company.name,
                    })
                  : this.translateService.instant('dashboard.celebratingFirstYear', { company: employee.company.name });
            const eventType = isFirstDay ? 'newHire' : 'anniversary';

            return {
                employeeId: employee.id,
                pseudoEmployee: {
                    avatarId: employee.avatarId,
                    firstName: employee.firstName,
                    lastName: employee.lastName,
                },
                upcomingEventTitle: text,
                upcomingEventDescription: description,
                startAt: date,
                endAt: endOfDay(date),
                type: eventType,
            };
        });
    }

    private putEventsInDays(): void {
        this.days = this.days.map((day) => {
            day.events = this.events.filter((event) => {
                return areRangesOverlapping(
                    day.date,
                    endOfDay(day.date),
                    parse(event.startAt),
                    subMinutes(endOfDay(parse(event.endAt)), 1)
                );
            });
            return day;
        });
    }

    private putImprovedEventsInDays(): void {
        let next7DaysEvents: CalendarEvent[] = [];
        this.firstTwoDays = [];
        this.next7Days = [];

        this.days.forEach((day, index) => {
            day.events = this.events.filter((event) =>
                areRangesOverlapping(
                    day.date,
                    endOfDay(day.date),
                    parse(event.startAt),
                    subMinutes(endOfDay(parse(event.endAt)), 1)
                )
            );

            if (index < 2) {
                day.events = this.sortEvents(day.events);
                this.firstTwoDays.push(day);
            } else {
                this.next7Days.push(day);
                next7DaysEvents = next7DaysEvents.concat(day.events);
            }
        });

        this.next7DaysDateGrouping.date = addDays(new Date(), 2);
        next7DaysEvents = this.removeDuplicateEvents(next7DaysEvents);
        this.next7DaysDateGrouping.events = this.sortEvents(next7DaysEvents);
    }

    private removeDuplicateEvents(events: CalendarEvent[]): CalendarEvent[] {
        const uniqueEventsMap = new Map<string, CalendarEvent>();

        events.forEach((event) => {
            if (event.type === 'timeOffRequest') {
                const key = `${event.startAt}_${event.endAt}`;

                if (!uniqueEventsMap.has(key)) {
                    uniqueEventsMap.set(key, event);
                }
            } else {
                uniqueEventsMap.set(JSON.stringify(event), event);
            }
        });

        return Array.from(uniqueEventsMap.values());
    }

    private sortEvents(events: CalendarEvent[]): CalendarEvent[] {
        const eventCategories = new Map<string, CalendarEvent[]>();

        events.forEach((event) => {
            if (!eventCategories.has(event.type)) {
                eventCategories.set(event.type, []);
            }

            const category = eventCategories.get(event.type);
            if (category) {
                category.push(event);
            }
        });

        const sortedEvents = Array.from(eventCategories.values())
            .reduce((accumulator, currentValue) => accumulator.concat(currentValue), [])
            .sort((a, b) => {
                const eventOrder = ['birthday', 'newHire', 'anniversary', 'timeOffRequests'];
                return (
                    eventOrder.indexOf(a.type) - eventOrder.indexOf(b.type) ||
                    new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
                );
            });

        return sortedEvents;
    }

    private async getSavedFilters(): Promise<LocalStorageFilters[]> {
        return (await this.localStorage.retrieve<LocalStorageFilters[]>(localStorageDashboardEventFiltersKey)) ?? [];
    }
}
